import { useState, useCallback, useEffect } from 'react';
import {
  getClinicInfo,
  getBranches,
  getBranchContacts,
  getHowToGet,
  getPromotions,
  getNews,
  Clinic,
  Branch,
  BranchContacts,
  HowToGet,
  Post,
} from '../services/clinicService';

export const useClinic = () => {
  const [clinic, setClinic] = useState<Clinic | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [branchContacts, setBranchContacts] = useState<BranchContacts | null>(null);
  const [howToGet, setHowToGet] = useState<HowToGet | null>(null);
  const [promotions, setPromotions] = useState<Post[]>([]);
  const [news, setNews] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch clinic info, branches and news on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const [clinicData, branchesData, newsData] = await Promise.all([
          getClinicInfo(),
          getBranches(),
          getNews().catch(() => ({ posts: [] })),
        ]);
        setClinic(clinicData.clinic);
        setBranches(branchesData.branches);
        setNews(newsData.posts);
      } catch (err: any) {
        setError(err.message || 'Ошибка загрузки данных клиники');
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  // Select branch and get contacts
  const handleSelectBranch = useCallback(async (branch: Branch) => {
    setSelectedBranch(branch);
    try {
      const [contactsData, howToGetData] = await Promise.all([
        getBranchContacts(branch.id),
        getHowToGet(branch.id).catch(() => ({ description: '', mapImages: [] })),
      ]);
      setBranchContacts(contactsData.contacts);
      setHowToGet(howToGetData);
    } catch (error) {
      console.error('Error loading branch details:', error);
    }
  }, []);

  // Fetch promotions
  const loadPromotions = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getPromotions();
      setPromotions(data.posts);
    } catch (err: any) {
      setError(err.message || 'Error loading promotions');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch news
  const loadNews = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getNews();
      setNews(data.posts);
    } catch (err: any) {
      setError(err.message || 'Error loading news');
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    // Data
    clinic,
    branches,
    selectedBranch,
    branchContacts,
    howToGet,
    promotions,
    news,

    // Loading states
    isLoading,

    // Error states
    error,

    // Actions
    handleSelectBranch,
    loadPromotions,
    loadNews,
  };
};
